import { useSdk } from '~/sdk';
import type { 
  NewsletterSubscriptionRequest, 
  NewsletterSubscriptionResponse,
  NewsletterStatusRequest,
  NewsletterStatusResponse,
  NewsletterError 
} from './types';

export const useNewsletter = () => {
  const sdk = useSdk();
  const { t } = useI18n();

  /**
   * Subscribe to newsletter
   */
  const subscribe = async (email: string, newsletter_language?: string): Promise<NewsletterSubscriptionResponse> => {
    try {
      const response = await sdk.commerce.subscribeToNewsletter({ email, newsletter_language });
      
      if (response.success && response.psdata?.success) {
        return {
          success: true,
          message: response.psdata.message || t('newsletter.success_message'),
          email: response.psdata.email || email
        };
      } else {
        throw new Error(response.psdata?.message || t('newsletter.error_subscription_failed'));
      }
    } catch (error: any) {
      console.error('Newsletter subscription error:', error);
      throw {
        message: error.message || t('newsletter.error_subscription_failed'),
        code: error.response?.status
      } as NewsletterError;
    }
  };

  /**
   * Check newsletter subscription status
   */
  const getStatus = async (email: string): Promise<NewsletterStatusResponse> => {
    try {
      const response = await sdk.commerce.getNewsletterStatus({ email });
      
      if (response.success && response.psdata) {
        return {
          email: response.psdata.email || email,
          subscribed: response.psdata.subscribed || false,
          customer_exists: response.psdata.customer_exists || false
        };
      } else {
        throw new Error('Failed to get newsletter status');
      }
    } catch (error: any) {
      console.error('Newsletter status check error:', error);
      throw {
        message: error.message || 'Failed to check newsletter status',
        code: error.response?.status
      } as NewsletterError;
    }
  };

  /**
   * Unsubscribe from newsletter
   */
  const unsubscribe = async (email: string): Promise<NewsletterSubscriptionResponse> => {
    try {
      const response = await sdk.commerce.unsubscribeFromNewsletter({ email });
      
      if (response.success && response.psdata?.success) {
        return {
          success: true,
          message: response.psdata.message || 'Successfully unsubscribed from newsletter',
          email: response.psdata.email || email
        };
      } else {
        throw new Error(response.psdata?.message || 'Failed to unsubscribe from newsletter');
      }
    } catch (error: any) {
      console.error('Newsletter unsubscription error:', error);
      throw {
        message: error.message || 'Failed to unsubscribe from newsletter',
        code: error.response?.status
      } as NewsletterError;
    }
  };

  /**
   * Validate email format
   */
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  return {
    subscribe,
    getStatus,
    unsubscribe,
    validateEmail
  };
};